import { Component, OnInit }  from '@angular/core';
import { Router }             from '@angular/router';
import { Subscription }       from 'rxjs/Subscription';

import * as providers from '../../providers/providers';
import * as models    from '../../models/models';

declare var AudioContext;
declare var webkitAudioContext;

const NOTE_REGEX = /(1|2|4|8|16|32|64)?((?:[a-g]|h|p)#?){1}(\.?)(4|5|6|7)?/;

const ALLOWED_DURATION = [1, 2, 4, 8, 16, 32];
const ALLOWED_OCTAVE   = [4, 5, 6, 7];
const ALLOWED_BPM      = [
      25, 28, 31, 35, 40, 45, 50, 56, 63, 70, 80, 90, 100,
      112, 125, 140, 160, 180, 200, 225, 250, 285, 320, 355,
      400, 450, 500, 565, 635, 715, 800, 900
];
const PITCHES = ["c", "c#", "d"," d#", "e", "f", "f#", "g", "g#", "a", "a#", "b"]

@Component({
  selector: 'page-account',
  templateUrl: 'account.html'
})
export class AccountPage implements OnInit {
  user: models.FirebaseUser = null;
  playing: boolean = false;
  validRTTTL: boolean = true;
  player: any;
  songStart: number = 0;
  songLen: number = 0;

  private audioContext: any  =   new (AudioContext || webkitAudioContext)();

  constructor(private router: Router, private db: providers.StamplingDB) {}

  ngOnInit() {
    this.user = this.db.getLoggedUser();
  }

  set rtttl(r) {
    this.user.tone = r
    this._parse_rttl(this.user.tone)
        .then((r) => this.validRTTTL=true)
        .catch((err) => this.validRTTTL=false);
  }

  get rtttl() {
    return this.user.tone;
  }

  play() {
    if (!this.playing) {
      this.playing = true;
      this._parse_rttl(this.user.tone)
        .then((r) => this._play(r))
        .catch((err) => alert(err));
    }
  }

  save() {
    if (this.validRTTTL) {
      console.log("Store tone")
      this.db.saveTone(this.user.id, this.user.tone)
    } else {
      alert("Is not a valid Tone");
    }
    if (this.user.workday!=undefined && this.user.workday>0) {
      this.db.saveWorkingHours(this.user.id, this.user.workday)
    }
  }

  _parse_rttl(rtttl: string): Promise<RTTTL> {
    return new Promise((suc, err) => {
      let sections: string[] = rtttl.split(":");
      if (sections.length<3) {
        err("RTTTL string is expected to have 3 sections separated by ':'")
        return;
      }
      let name: string = sections[0];

      //Parse controls
      let cntr: RTTTLControls = {defaultOctave: 6, defaultNoteDuration:1/4, beatsPerMinute: 63};
      let cntrSection: string[] = sections[1].split(",");
      for (var i in cntrSection) {
        let info: string[] = cntrSection[i].split("=");
        if (info.length<2) continue;
        let val: number = parseInt(info[1]);
        switch (info[0]) {
          case "o":
            if (ALLOWED_OCTAVE.indexOf(val)>=0) cntr.defaultOctave = val;
            break;
          case "d":
            if (ALLOWED_DURATION.indexOf(val)>=0) cntr.defaultNoteDuration = 1/val;
            break;
          case "b":
            if (ALLOWED_BPM.indexOf(val)>=0) cntr.beatsPerMinute = val;
            break;
          default:
            err("undefined control field "+info[0])
            return;
        }
      }
      //Parse notes
      let notes: RTTTLNote[] = [];
      let noteSection: string[] = sections[2].split(",");
      for (var i in noteSection) {
        let note = noteSection[i].match(NOTE_REGEX);
        let noteName: string = note[2] === 'h' ? 'b' : note[2];
        let noteOctave: number = parseInt(note[4]) || cntr.defaultOctave;
        let duration: number = 1/parseInt(note[1]) || cntr.defaultNoteDuration;
        if (note[3] === '.') duration *= 1.5;

        let n: RTTTLNote = {
          note: noteName,
          octave: noteOctave,
          rest: noteName==='p',
          duration: duration,
          seconds: (duration*4) * (60/cntr.beatsPerMinute),
          frequency: this._calculateFrequency(noteName, noteOctave),
          rtttl: noteSection[i]
        }
        notes.push(n);
      }
      //Done
      suc({name: name, controls: cntr, notes: notes, rtttl: rtttl});
    })

  }

  _calculateFrequency(note: string, octave: number): number {
    if (note === 'p') return 0;
    return 440 * 2**(((octave - 4)*PITCHES.length + PITCHES.indexOf(note)) / PITCHES.length)
  }

  _play (music: RTTTL) {
    console.log(music);
    let time = this.audioContext.currentTime;
    this.songStart = time;
    var master = this.audioContext.createGain()
    master.connect(this.audioContext.destination)
    master.gain.value = 0.1

    var noteVolume = this.audioContext.createGain()
    noteVolume.connect(master)
    noteVolume.gain.value = 0

    var osc = this.audioContext.createOscillator()
    osc.start(0)
    osc.connect(noteVolume)

    osc.frequency.cancelScheduledValues(time)
	  noteVolume.gain.cancelScheduledValues(time)
	  noteVolume.gain.setValueAtTime(0, time)
    noteVolume.gain.linearRampToValueAtTime(0, time + 0.01)

    time = this.audioContext.currentTime + 0.05
    for (var i=0; i<music.notes.length; i++) {
      let note: RTTTLNote = music.notes[i];
      if (!note.rest) {
        osc.frequency.setValueAtTime(note.frequency, time)
  			noteVolume.gain.setValueAtTime(0, time)
  			noteVolume.gain.linearRampToValueAtTime(1, time + 0.001)
  			noteVolume.gain.exponentialRampToValueAtTime(1, time + note.seconds-0.01-0.04)
  			noteVolume.gain.linearRampToValueAtTime(0, time + note.seconds -0.01)
      }
      time += note.seconds;
    }
    this.songLen = time;
    this.player = setInterval(() => {
      if (this.audioContext.currentTime >= this.songLen) {
        clearInterval(this.player);
        this.playing = false;
      }
    }, 100);
  }
}

export interface RTTTL {
  name: string,
  rtttl: string,
  controls: RTTTLControls,
  notes: RTTTLNote[]
}

export interface RTTTLControls{
  defaultOctave: number, defaultNoteDuration:number, beatsPerMinute: number
}

export interface RTTTLNote {
  note: string;
  octave: number;
  duration: number;
  rest: boolean;

  seconds: number;
  frequency: number;

  rtttl: string;
}



// WEBPACK FOOTER //
// /builds/mediaclinicstn/stampling-viewer/src/pages/account/account.ts